const ESCO_BASE_URL = process.env.REACT_APP_ESCO_BASE_URL || 'https://ec.europa.eu/esco/api';
const ESCO_RESOURCE_BASE_URL = process.env.REACT_APP_ESCO_RESOURCE_BASE_URL || ESCO_BASE_URL;
const DEFAULT_LANGUAGE = 'en';
const DEFAULT_LIMIT = 20;
const DEFAULT_TIMEOUT_MS = 12000;

const CATEGORY_ALIASES = {
	it: 'information technology',
	ict: 'information technology',
	engineering: 'engineer',
	health: 'healthcare',
	marketing: 'marketing',
	sales: 'sales',
	finance: 'finance',
	education: 'education',
	construction: 'construction',
	hospitality: 'hospitality',
};

function resolveCategoryText(category) {
	if (!category || typeof category !== 'string') {
		return '';
	}

	const normalized = category.trim().toLowerCase();
	return CATEGORY_ALIASES[normalized] || normalized;
}

function toTitleCase(value) {
	if (typeof value !== 'string' || !value) {
		return '';
	}

	return value
		.trim()
		.split(/\s+/)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

function normalizeKey(value) {
	return String(value || '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '');
}

function getLocalizedText(value, language = DEFAULT_LANGUAGE) {
	if (typeof value === 'string') {
		return value.trim();
	}

	if (Array.isArray(value)) {
		for (const entry of value) {
			const text = getLocalizedText(entry, language);
			if (text) {
				return text;
			}
		}

		return '';
	}

	if (value && typeof value === 'object') {
		const directValue = value?.[language] || value?.en || value?.[DEFAULT_LANGUAGE];
		if (directValue) {
			return getLocalizedText(directValue, language);
		}

		for (const key of ['text', 'title', 'label', 'name', 'value', 'description', 'definition', 'scopeNote', 'preferredLabel', 'preferredTerm']) {
			if (value[key] !== undefined && value[key] !== null) {
				const text = getLocalizedText(value[key], language);
				if (text) {
					return text;
				}
			}
		}

		for (const nestedValue of Object.values(value)) {
			const text = getLocalizedText(nestedValue, language);
			if (text) {
				return text;
			}
		}
	}

	return '';
}

function collectMatchingNodes(source, keyParts, results = [], seen = new WeakSet()) {
	if (!source || typeof source !== 'object' || seen.has(source)) {
		return results;
	}

	seen.add(source);

	if (Array.isArray(source)) {
		source.forEach((item) => collectMatchingNodes(item, keyParts, results, seen));
		return results;
	}

	Object.entries(source).forEach(([key, value]) => {
		const normalizedKey = normalizeKey(key);
		if (keyParts.some((part) => normalizedKey.includes(part))) {
			results.push(value);
		}

		if (value && typeof value === 'object') {
			collectMatchingNodes(value, keyParts, results, seen);
		}
	});

	return results;
}

function normalizeRelatedItem(item, language = DEFAULT_LANGUAGE) {
	if (!item) {
		return '';
	}

	if (typeof item === 'string') {
		return getLocalizedText(item, language);
	}

	if (Array.isArray(item)) {
		for (const entry of item) {
			const text = normalizeRelatedItem(entry, language);
			if (text) {
				return text;
			}
		}

		return '';
	}

	const title =
		getLocalizedText(item?.preferredLabel, language) ||
		getLocalizedText(item?.preferredTerm, language) ||
		getLocalizedText(item?.title, language) ||
		getLocalizedText(item?.label, language) ||
		getLocalizedText(item?.name, language) ||
		getLocalizedText(item?.code, language) ||
		getLocalizedText(item?.uri, language);

	const description =
		getLocalizedText(item?.description, language) ||
		getLocalizedText(item?.definition, language) ||
		getLocalizedText(item?.scopeNote, language);

	return title || description || '';
}

function normalizeRelatedCollection(source, keyParts, language = DEFAULT_LANGUAGE) {
	const nodes = collectMatchingNodes(source, keyParts);
	const values = nodes.flatMap((node) => (Array.isArray(node) ? node : [node]));
	const normalizedValues = values
		.map((item) => normalizeRelatedItem(item, language))
		.filter(Boolean);

	return Array.from(new Set(normalizedValues));
}

function normalizeOccupationDetails(item = {}, language = DEFAULT_LANGUAGE) {
	const description =
		getLocalizedText(item?.description?.definition, language) ||
		getLocalizedText(item?.description?.scopeNote, language) ||
		getLocalizedText(item?.definition, language) ||
		getLocalizedText(item?.scopeNote, language) ||
		getLocalizedText(item?.description, language);

	return {
		description,
		essentialSkillsAndCompetences: normalizeRelatedCollection(item, [
			'essentialskillsandcompetence',
			'essentialskillandcompetence',
			'essentialskills',
			'essentialskill',
		], language),
		essentialKnowledge: normalizeRelatedCollection(item, [
			'essentialknowledge',
			'knowledgeessential',
		], language),
		optionalKnowledge: normalizeRelatedCollection(item, [
			'optionalknowledge',
			'knowledgeoptional',
		], language),
		optionalSkillsAndCompetences: normalizeRelatedCollection(item, [
			'optionalskillsandcompetence',
			'optionalskillandcompetence',
			'optionalskills',
			'optionalskill',
		], language),
	};
}

function normalizeOccupationResource(item = {}, language = DEFAULT_LANGUAGE) {
	const base = normalizeSearchResult(item, language);
	const details = normalizeOccupationDetails(item, language);

	return {
		...base,
		...details,
		description: details.description || base.description,
	};
}

function buildUrl(baseUrl, path, queryParams = {}) {
	const url = new URL(`${baseUrl}${path}`);

	Object.entries(queryParams).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			url.searchParams.set(key, String(value));
		}
	});

	return url.toString();
}

async function fetchJson(url, { timeoutMs = DEFAULT_TIMEOUT_MS, signal } = {}) {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

	const onAbort = () => controller.abort();
	if (signal) {
		if (signal.aborted) {
			controller.abort();
		} else {
			signal.addEventListener('abort', onAbort, { once: true });
		}
	}

	try {
		const response = await fetch(url, {
			method: 'GET',
			headers: {
				Accept: 'application/json',
			},
			signal: controller.signal,
		});

		if (!response.ok) {
			const body = await response.text();
			throw new Error(`ESCO request failed (${response.status}): ${body || response.statusText}`);
		}

		return await response.json();
	} catch (error) {
		if (error.name === 'AbortError') {
			throw new Error('ESCO request timed out or was aborted.');
		}

		throw error;
	} finally {
		clearTimeout(timeoutId);
		if (signal) {
			signal.removeEventListener('abort', onAbort);
		}
	}
}

function normalizeSearchResult(item = {}, language = DEFAULT_LANGUAGE) {
	const title = getLocalizedText(item?.preferredLabel, language) || getLocalizedText(item?.title, language) || '';
	const description = getLocalizedText(item?.searchHit, language) || getLocalizedText(item?.description, language) || '';

	return {
		id: item?.uri || item?.code || item?.id || null,
		uri: item?.uri || null,
		code: item?.code || null,
		title: toTitleCase(title),
		description: description ? description.charAt(0).toUpperCase() + description.slice(1) : '',
		type: item?.className || item?.type || 'occupation',
	};
}

export async function searchJobsByCategory(category, options = {}) {
	const {
		language = DEFAULT_LANGUAGE,
		limit = DEFAULT_LIMIT,
		offset = 0,
		selectedVersion,
		timeoutMs = DEFAULT_TIMEOUT_MS,
		signal,
	} = options;

	const text = resolveCategoryText(category);
	if (!text) {
		return {
			total: 0,
			offset,
			limit,
			results: [],
			raw: null,
		};
	}

	const url = buildUrl(ESCO_BASE_URL, '/search', {
		type: 'occupation',
		text,
		language,
		limit,
		offset,
		selectedVersion,
	});

	const data = await fetchJson(url, { timeoutMs, signal });
	const items = data?._embedded?.results || [];

	return {
		total: Number(data?.total) || items.length,
		offset: Number(data?.offset) || offset,
		limit: Number(data?.limit) || limit,
		results: items.map((item) => normalizeSearchResult(item, language)),
		raw: data,
	};
}

export async function getJobByUri(uri, options = {}) {
	const {
		language = DEFAULT_LANGUAGE,
		selectedVersion,
		timeoutMs = DEFAULT_TIMEOUT_MS,
		signal,
	} = options;

	if (!uri) {
		return null;
	}

	const candidateBaseUrls = Array.from(new Set([
		ESCO_RESOURCE_BASE_URL,
		ESCO_BASE_URL,
	]));

	let lastError = null;

	for (const baseUrl of candidateBaseUrls) {
		try {
			const url = buildUrl(baseUrl, '/resource/occupation', {
				uri,
				language,
				selectedVersion,
			});

			const data = await fetchJson(url, { timeoutMs, signal });
			return normalizeOccupationResource(data, language);
		} catch (error) {
			lastError = error;
		}
	}

	throw lastError;
}

export async function loadJobsByCategory(category, options = {}) {
	const { includeDetails = false, ...searchOptions } = options;
	const searchResult = await searchJobsByCategory(category, searchOptions);

	if (!includeDetails || !searchResult.results.length) {
		return searchResult;
	}

	const detailedResults = await Promise.all(
		searchResult.results.map(async (job) => {
			if (!job.uri) {
				return job;
			}

			try {
				const details = await getJobByUri(job.uri, searchOptions);
				return {
					...job,
					...details,
				};
			} catch {
				return job;
			}
		})
	);

	return {
		...searchResult,
		results: detailedResults,
	};
}

export const escoJobsUtils = {
	searchJobsByCategory,
	getJobByUri,
	loadJobsByCategory,
};

export {
	ESCO_BASE_URL,
	ESCO_RESOURCE_BASE_URL,
	DEFAULT_LANGUAGE,
	DEFAULT_LIMIT,
	DEFAULT_TIMEOUT_MS,
};
